import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  Snackbar, Alert,
} from '@mui/material';
import { buscarKeyChatGPT, melhorarTextService } from '../../../../services/core/processos/processosService';

const PERGUNTA = 'Reescreva o texto em português formal seguindo as condições: Não altere as tags '
  + 'marcadas com <$tag$> caso existam e mantenha o formato do texto, caso seja enviado um HTML '
  + 'também retorne um HTML. Texto';
const MENSAGEM_ERRO = 'Erro ao reescrever texto.';

/**
 * Sub-dialog "Reescrever" (IA) — Etapa 7 do plano `docs/architecture/
 * plano-aw2-1054-aba-historico.md`. Port de `DialogGPT.jsx` real para MUI
 * v5: mesmo prompt fixo, mesma dupla chamada `buscarKeyChatGPT()` →
 * `melhorarTextService(texto, key)`. Decisão confirmada com o usuário antes
 * desta etapa: portar EXATAMENTE como está em produção — a chamada à API da
 * OpenAI já é feita direto do browser com uma key temporária obtida do
 * backend, expondo a key no painel de rede durante a chamada; esse risco já
 * é aceito hoje em produção e não é introduzido por este port (ver Análise
 * de Segurança do plano).
 *
 * Diferença deliberada da tela real: `outputText` é renderizado como texto
 * puro (não `html-react-parser`/`parse()`) — a tela real usa isso porque
 * seus campos podem ser templates HTML com tags `<$tag$>`; os campos
 * Anotação/Relatório do protótipo são texto livre simples, então parsear e
 * renderizar HTML arbitrário vindo da resposta do modelo abriria uma
 * superfície nova (tags/atributos arbitrários) sem nenhum ganho funcional
 * aqui — reduz risco sem perder paridade de comportamento observável.
 * Também sem `react-draggable` (a tela real arrasta o dialog) — não
 * essencial ao fluxo, e nenhum outro dialog do protótipo usa esse padrão.
 *
 * Guard "sessão monotônica" (`sessaoRef`), incrementado só na transição real
 * fechado→aberto (`foiAbertoRef`, mesmo padrão de `DialogEnviarEmailDevedor.jsx`
 * documentado em `src/prototipo/CLAUDE.md`) — invalida uma resposta tardia
 * de uma chamada anterior (abrir, "Tentar Novamente") que ainda não resolveu
 * quando o dialog fecha e reabre. Guard síncrono (`carregandoRef`) contra
 * duplo-clique em "Tentar Novamente" antes do primeiro `setCarregando(true)`
 * refletir no render (mesma categoria de bug corrigido no favoritar da
 * Etapa 6).
 */
export default function DialogReescrever({
  open, texto, onClose, onAceitar,
}) {
  const [outputText, setOutputText] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const sessaoRef = useRef(0);
  const carregandoRef = useRef(false);
  const foiAbertoRef = useRef(false);
  const textoAtualRef = useRef(texto);

  async function melhorarTexto() {
    if (carregandoRef.current) return;
    const sessaoAlvo = sessaoRef.current;
    carregandoRef.current = true;
    setCarregando(true);
    setErro('');
    try {
      const textoLimpo = textoAtualRef.current.replace(/\n+/g, '\n');
      const apiKey = await buscarKeyChatGPT();
      const response = await melhorarTextService(`${PERGUNTA}: ${textoLimpo}`, apiKey);
      if (sessaoRef.current !== sessaoAlvo) return;
      const novoTexto = response.data.choices[0].message.content;
      setOutputText(novoTexto);
      textoAtualRef.current = novoTexto;
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) return;
      setOutputText('');
      setErro(MENSAGEM_ERRO);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        carregandoRef.current = false;
        setCarregando(false);
      }
    }
  }

  useEffect(() => {
    if (open && !foiAbertoRef.current) {
      sessaoRef.current += 1;
      // Reset também da guarda síncrona, não só do contador de sessão: sem
      // isso, fechar o dialog com uma chamada em voo e reabrir antes dela
      // resolver trava `carregandoRef.current` em `true` para sempre — o
      // `finally` de `melhorarTexto` da chamada antiga vê a sessão como
      // inválida e pula o reset (mesma regra já documentada em
      // src/prototipo/CLAUDE.md para `useRef` de guarda em efeitos de reset
      // por sessão/contexto).
      carregandoRef.current = false;
      setCarregando(false);
      textoAtualRef.current = texto;
      setOutputText('');
      setErro('');
      melhorarTexto();
    }
    foiAbertoRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function onClickAceitar() {
    onAceitar(outputText);
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reescrever</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
          {carregando ? 'Reescrevendo...' : outputText}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={melhorarTexto} disabled={carregando}>Tentar Novamente</Button>
        <div>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={onClickAceitar} disabled={carregando || !outputText}>
            Aceitar
          </Button>
        </div>
      </DialogActions>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErro('')}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

DialogReescrever.propTypes = {
  open: PropTypes.bool.isRequired,
  texto: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onAceitar: PropTypes.func.isRequired,
};
